import type { BranchWithSettingsResult } from "@/modules/branches/domain/types";
import { usePickupSlotsQuery } from "@/modules/branches/presentation/api/queries";
import { BranchBlackoutEditorCard } from "@/modules/branches/presentation/ui/BranchBlackoutEditor";
import {
  BranchScheduleSchema,
  type BranchScheduleFormValues,
  type BranchScheduleSubmitPayload,
} from "@/modules/branches/presentation/ui/branch-schedule-types";
import { FormRoot, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";


import {
  ClockIcon,
  EyeIcon,
  GlobeIcon,
  PackageIcon,
  PlusIcon,
  ShoppingBagIcon,
  Trash2Icon,
  TruckIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useFieldArray } from "react-hook-form";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const DAY_LABELS: Record<(typeof DAY_KEYS)[number], string> = {
  mon: "ຈັນ",
  tue: "ອັງຄານ",
  wed: "ພຸດ",
  thu: "ພະຫັດ",
  fri: "ສຸກ",
  sat: "ເສົາ",
  sun: "ອາທິດ",
};

export type { BranchScheduleFormValues };

function blackoutsFromSettings(
  data: BranchWithSettingsResult | null,
): BranchScheduleFormValues["blackouts"] {
  return (data?.settings?.blackoutDatesJson ?? []).map((b) => ({
    id: b.id ?? crypto.randomUUID(),
    date: b.date,
    allDay: b.allDay ?? true,
    startTime: b.startTime ?? "",
    endTime: b.endTime ?? "",
    reason: b.reason ?? "",
  }));
}

function defaultOpenHours(): Record<
  string,
  { enabled: boolean; open: string; close: string }
> {
  const ent = { enabled: true, open: "07:00", close: "19:00" };
  return Object.fromEntries(DAY_KEYS.map((d) => [d, { ...ent }]));
}

function openHoursFromBranch(
  data: BranchWithSettingsResult | null,
): Record<string, { enabled: boolean; open: string; close: string }> {
  const def = defaultOpenHours();
  const json = data?.openHoursJson;
  if (!json) return def;
  return DAY_KEYS.reduce(
    (acc, key) => {
      const v = json[key];
      const d = def[key] ?? { enabled: true, open: "07:00", close: "19:00" };
      if (v)
        acc[key] = {
          enabled: true,
          open: v.open,
          close: v.close,
        };
      else
        acc[key] = {
          enabled: false,
          open: d.open,
          close: d.close,
        };
      return acc;
    },
    {} as Record<string, { enabled: boolean; open: string; close: string }>,
  );
}

/** สร้าง slot labels (เช่น 09:00, 09:30) จาก open/close และ duration (นาที) */
function buildSlotPreview(
  open: string,
  close: string,
  durationMinutes: number,
  prepBufferMinutes: number,
): string[] {
  const [openH = 0, openM = 0] = open.split(":").map(Number);
  const [closeH = 0, closeM = 0] = close.split(":").map(Number);
  const startM = openH * 60 + openM;
  let endM = closeH * 60 + closeM;
  if (endM <= startM) endM += 24 * 60;
  const slots: string[] = [];
  for (
    let t = startM;
    t + durationMinutes + prepBufferMinutes <= endM;
    t += durationMinutes
  ) {
    const h = Math.floor(t / 60) % 24;
    const m = t % 60;
    slots.push(
      `${String(h ?? 0).padStart(2, "0")}:${String(m ?? 0).padStart(2, "0")}`,
    );
  }
  return slots;
}

function formatSlotLabel(s: string): string {
  const [h = 0, m = 0] = s.split(":").map(Number);
  if (h >= 12)
    return `${h === 12 ? 12 : h - 12}:${String(m).padStart(2, "0")} PM`;
  return `${h || 12}:${String(m).padStart(2, "0")} AM`;
}

export function BranchScheduleForm({
  branchId,
  data,
  onSubmit,
  onDiscard,
  submitting,
}: {
  branchId?: string;
  data: BranchWithSettingsResult | null;
  onSubmit: (payload: BranchScheduleSubmitPayload) => void;
  onDiscard: () => void;
  submitting?: boolean;
}) {
  const s = data?.settings;
  const openHoursInit = openHoursFromBranch(data);
  const slotModeInit =
    s?.slotConfigJson?.slots && s.slotConfigJson.slots.length > 0
      ? "slots"
      : "asap";

  const defaultValues: BranchScheduleFormValues = {
    openHours: openHoursInit,
    onlineOrdersEnabled: s?.onlineOrdersEnabled ?? true,
    pickupEnabled: s?.pickupEnabled ?? true,
    deliveryEnabled: s?.deliveryEnabled ?? false,
    autoAccept: s?.autoAccept ?? false,
    slotMode: slotModeInit,
    capacityPerSlot: s?.capacityPerSlot ?? 10,
    cutoffMinutes: s?.cutoffMinutes ?? 30,
    prepBufferMinutes: s?.prepBufferMinutes ?? 15,
    slotDurationMinutes: s?.slotConfigJson?.slotDurationMinutes ?? 30,
    slotRanges: s?.slotConfigJson?.slots ?? [
      { start: "09:00", end: "12:00" },
      { start: "14:00", end: "18:00" },
    ],
    blackouts: blackoutsFromSettings(data),
  };

  const methods = RHF.useForm<BranchScheduleFormValues>({
    resolver: zodResolver(BranchScheduleSchema),
    defaultValues,
  });

  const slotRangeFields = useFieldArray({
    control: methods.control,
    name: "slotRanges",
  });

  const openHours = methods.watch("openHours");
  const slotMode = methods.watch("slotMode");
  const onlineOrdersEnabled = methods.watch("onlineOrdersEnabled");
  const slotDurationMinutes = methods.watch("slotDurationMinutes") ?? 30;
  const prepBufferMinutes = methods.watch("prepBufferMinutes") ?? 15;
  const [previewDayIndex, setPreviewDayIndex] = useState(0);
  const [previewDate, setPreviewDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );

  const pickupSlotsQuery = usePickupSlotsQuery(branchId ?? "", previewDate);

  const previewDayKey = DAY_KEYS[previewDayIndex] ?? "mon";
  const previewSlots = useMemo(() => {
    const day = openHours?.[previewDayKey];
    if (!day || !day.enabled) return [];
    return buildSlotPreview(
      day.open,
      day.close,
      slotDurationMinutes,
      prepBufferMinutes,
    );
  }, [openHours, previewDayKey, slotDurationMinutes, prepBufferMinutes]);

  const { morningSlots, afternoonSlots } = useMemo(() => {
    const morning = previewSlots.filter((slot) => slot < "12:00");
    const afternoon = previewSlots.filter((slot) => slot >= "12:00");
    return { morningSlots: morning, afternoonSlots: afternoon };
  }, [previewSlots]);

  const applyToAllDays = () => {
    const defMon = defaultOpenHours().mon as {
      enabled: boolean;
      open: string;
      close: string;
    };
    const first = openHours?.mon ?? defMon;
    const next = DAY_KEYS.reduce(
      (acc, key) => {
        acc[key] = {
          enabled: first.enabled,
          open: first.open ?? defMon.open,
          close: first.close ?? defMon.close,
        };
        return acc;
      },
      {} as Record<string, { enabled: boolean; open: string; close: string }>,
    );
    methods.setValue("openHours", next);
  };

  const openHoursToJson = (
    hours: Record<string, { enabled: boolean; open: string; close: string }>,
  ): Record<string, { open: string; close: string } | null> =>
    Object.fromEntries(
      DAY_KEYS.map((key) => {
        const v = hours[key];
        if (!v || !v.enabled) return [key, null];
        return [key, { open: v.open, close: v.close }];
      }),
    );

  return (
    <FormRoot<BranchScheduleFormValues>
      methods={methods}
      onSubmit={(vals) => {
        const slotConfigJson =
          vals.slotMode === "slots" && vals.slotRanges?.length
            ? {
                slotDurationMinutes: vals.slotDurationMinutes ?? 30,
                slots: vals.slotRanges,
              }
            : null;
        onSubmit({
          openHoursJson: openHoursToJson(vals.openHours),
          onlineOrdersEnabled: vals.onlineOrdersEnabled,
          pickupEnabled: vals.pickupEnabled,
          deliveryEnabled: vals.deliveryEnabled,
          autoAccept: vals.autoAccept,
          capacityPerSlot: vals.capacityPerSlot,
          cutoffMinutes: vals.cutoffMinutes,
          prepBufferMinutes: vals.prepBufferMinutes,
          slotConfigJson,
          blackoutDatesJson: vals.blackouts.length
            ? vals.blackouts.map((b) => ({
                id: b.id,
                date: b.date,
                allDay: b.allDay,
                startTime: b.allDay ? null : b.startTime || null,
                endTime: b.allDay ? null : b.endTime || null,
                reason: b.reason?.trim() ? b.reason.trim() : null,
              }))
            : null,
        });
      }}
      className="space-y-6"
    >
      <Card className="rounded-xl border bg-card">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-6">
          <div className="flex items-center gap-3">
            <GlobeIcon className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">ຮັບອໍເດີອອນໄລນ</p>
              <p className="text-muted-foreground text-sm">
                ເປີດຫຼືປິດການຮັບອໍເດີຈາກແອັບລູກຄ້າ
              </p>
            </div>
          </div>
          <RHF.Controller
            name="onlineOrdersEnabled"
            control={methods.control}
            render={({ field }) => (
              <label
                htmlFor="schedule-online-orders-enabled"
                className="flex cursor-pointer items-center gap-2"
              >
                <Checkbox
                  id="schedule-online-orders-enabled"
                  checked={field.value}
                  onCheckedChange={(val) => field.onChange(val === true)}
                />
                <span className="font-medium text-sm">
                  {field.value ? "ເປີດຮັບ" : "ປິດຮັບ"}
                </span>
              </label>
            )}
          />
        </CardContent>
      </Card>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left column */}
        <div className="flex-1 space-y-6">
          {/* Operating Hours */}
          <Card className="rounded-xl border bg-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <ClockIcon className="h-5 w-5 text-muted-foreground" />
                ເວລາເປີດໃຫ້ບໍລິການ
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={applyToAllDays}
              >
                ນຳໃຊ້ທຸກວັນ
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {DAY_KEYS.map((key) => (
                  <div
                    key={key}
                    className="flex flex-wrap items-center gap-3 border-b py-2 last:border-0"
                  >
                    <RHF.Controller
                      name={`openHours.${key}.enabled`}
                      control={methods.control}
                      render={({ field }) => (
                        <label
                          htmlFor={`openHours.${key}.enabled`}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <Checkbox
                            id={`openHours.${key}.enabled`}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                          <span className="font-medium">{DAY_LABELS[key]}</span>
                        </label>
                      )}
                    />
                    <RHF.Controller
                      name={`openHours.${key}.open`}
                      control={methods.control}
                      render={({ field }) => (
                        <input
                          type="time"
                          className="h-9 w-28 rounded-[12px] border border-input bg-background px-2 text-sm"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          disabled={!methods.watch(`openHours.${key}.enabled`)}
                        />
                      )}
                    />
                    <span className="text-muted-foreground text-sm">ເຖິງ</span>
                    <RHF.Controller
                      name={`openHours.${key}.close`}
                      control={methods.control}
                      render={({ field }) => (
                        <input
                          type="time"
                          className="h-9 w-28 rounded-[12px] border border-input bg-background px-2 text-sm"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          disabled={!methods.watch(`openHours.${key}.enabled`)}
                        />
                      )}
                    />
                    {methods.watch(`openHours.${key}.enabled`) ? (
                      <span className="text-green-600 text-sm">ເປີດ</span>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        ປິດວັນນີ້
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ວິທີຮັບອໍເດີ + ປະເພດ Slot + ຕັ້ງຄ່າ Slot */}
          <Card className="rounded-xl border bg-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">
                ວິທີຮັບອໍເດີ & ປະເພດ Slot & ຕັ້ງຄ່າ Slot
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ວິທີຮັບອໍເດີ */}
              <div className={onlineOrdersEnabled ? undefined : "pointer-events-none opacity-50"}>
                <p className="mb-2 font-medium text-muted-foreground text-sm">
                  ວິທີຮັບອໍເດີ
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <RHF.Controller
                    name="pickupEnabled"
                    control={methods.control}
                    render={({ field }) => (
                      <label
                        htmlFor="schedule-pickup-enabled"
                        className="flex cursor-pointer items-center justify-between rounded-[12px] border p-4"
                      >
                        <div className="flex items-center gap-3">
                          <ShoppingBagIcon className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">Pickup</p>
                            <p className="text-muted-foreground text-sm">
                              ຮັບຢູ່ຮ້ານ
                            </p>
                          </div>
                        </div>
                        <Checkbox
                          id="schedule-pickup-enabled"
                          checked={field.value}
                          onCheckedChange={(val) =>
                            field.onChange(val === true)
                          }
                        />
                      </label>
                    )}
                  />
                  <RHF.Controller
                    name="deliveryEnabled"
                    control={methods.control}
                    render={({ field }) => (
                      <label
                        htmlFor="schedule-delivery-enabled"
                        className="flex cursor-pointer items-center justify-between rounded-[12px] border p-4"
                      >
                        <div className="flex items-center gap-3">
                          <TruckIcon className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">Delivery</p>
                            <p className="text-muted-foreground text-sm">
                              ສົ່ງເຮືອນ
                            </p>
                          </div>
                        </div>
                        <Checkbox
                          id="schedule-delivery-enabled"
                          checked={field.value}
                          onCheckedChange={(val) =>
                            field.onChange(val === true)
                          }
                        />
                      </label>
                    )}
                  />
                  <RHF.Controller
                    name="autoAccept"
                    control={methods.control}
                    render={({ field }) => (
                      <label
                        htmlFor="schedule-auto-accept"
                        className="flex cursor-pointer items-center justify-between rounded-[12px] border p-4"
                      >
                        <div className="flex items-center gap-3">
                          <PackageIcon className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">ອະນຸມັດດ້ວຍມື</p>
                            <p className="text-muted-foreground text-sm">
                              ກວດອໍເດີກ່ອນຮັບ
                            </p>
                          </div>
                        </div>
                        <Checkbox
                          id="schedule-auto-accept"
                          checked={field.value}
                          onCheckedChange={(val) =>
                            field.onChange(val === true)
                          }
                        />
                      </label>
                    )}
                  />
                </div>
              </div>

              {/* ປະເພດ Slot */}
              <div>
                <p className="mb-2 font-medium text-muted-foreground text-sm">
                  ປະເພດ Slot
                </p>
                <RHF.Controller
                  name="slotMode"
                  control={methods.control}
                  render={({ field }) => (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={
                          field.value === "slots" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => field.onChange("slots")}
                      >
                        ເວລາລະບຸ (Time Slots)
                      </Button>
                      <Button
                        type="button"
                        variant={field.value === "asap" ? "default" : "outline"}
                        size="sm"
                        onClick={() => field.onChange("asap")}
                      >
                        ASAP ເທົ່ານັ້ນ
                      </Button>
                    </div>
                  )}
                />
              </div>

              {slotMode === "slots" ? (
                <div className="space-y-4 rounded-[12px] border bg-muted/20 p-4">
                  <p className="font-medium text-sm">ຊ່ວງເວລາ Slot</p>
                  <div>
                    <RHF.Controller
                      name="slotDurationMinutes"
                      control={methods.control}
                      render={({ field }) => (
                        <>
                          <label
                            htmlFor="schedule-slot-duration"
                            className="mb-1 block font-medium text-muted-foreground text-sm"
                          >
                            ຄວາມຍາວ Slot (ນາທີ)
                          </label>
                          <input
                            id="schedule-slot-duration"
                            type="number"
                            min={5}
                            step={5}
                            className="w-full max-w-[160px] rounded-[12px] border border-input bg-background px-3 py-2 text-sm"
                            value={field.value ?? 30}
                            onChange={(e) =>
                              field.onChange(
                                Number.parseInt(e.target.value, 10) || 30,
                              )
                            }
                          />
                        </>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    {slotRangeFields.fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="flex flex-wrap items-end gap-2"
                      >
                        <div>
                          <label
                            htmlFor={`slot-range-start-${field.id}`}
                            className="mb-1 block text-muted-foreground text-xs"
                          >
                            ເລີ່ມ
                          </label>
                          <RHF.Controller
                            name={`slotRanges.${index}.start`}
                            control={methods.control}
                            render={({ field: startField }) => (
                              <input
                                id={`slot-range-start-${field.id}`}
                                type="time"
                                className="h-9 w-28 rounded-[12px] border border-input bg-background px-2 text-sm"
                                value={startField.value}
                                onChange={(e) =>
                                  startField.onChange(e.target.value)
                                }
                              />
                            )}
                          />
                        </div>
                        <span className="pb-2 text-muted-foreground text-sm">
                          ເຖິງ
                        </span>
                        <div>
                          <label
                            htmlFor={`slot-range-end-${field.id}`}
                            className="mb-1 block text-muted-foreground text-xs"
                          >
                            ສິ້ນສຸດ
                          </label>
                          <RHF.Controller
                            name={`slotRanges.${index}.end`}
                            control={methods.control}
                            render={({ field: endField }) => (
                              <input
                                id={`slot-range-end-${field.id}`}
                                type="time"
                                className="h-9 w-28 rounded-[12px] border border-input bg-background px-2 text-sm"
                                value={endField.value}
                                onChange={(e) =>
                                  endField.onChange(e.target.value)
                                }
                              />
                            )}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => slotRangeFields.remove(index)}
                          disabled={slotRangeFields.fields.length <= 1}
                          aria-label="ລຶບຊ່ວງ slot"
                        >
                          <Trash2Icon className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        slotRangeFields.append({ start: "09:00", end: "17:00" })
                      }
                    >
                      <PlusIcon className="h-4 w-4" />
                      ເພີ່ມຊ່ວງເວລາ
                    </Button>
                  </div>
                </div>
              ) : null}

              {/* ຕັ້ງຄ່າ Slot */}
              <div>
                <p className="mb-2 font-medium text-muted-foreground text-sm">
                  ຕັ້ງຄ່າ Slot
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <RHF.Controller
                      name="capacityPerSlot"
                      control={methods.control}
                      render={({ field }) => (
                        <>
                          <label
                            htmlFor="schedule-capacity-per-slot"
                            className="mb-1 block font-medium text-muted-foreground text-sm"
                          >
                            ຈຳນວນຕໍ່ Slot
                          </label>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() =>
                                field.onChange(
                                  Math.max(0, (field.value ?? 10) - 1),
                                )
                              }
                            >
                              −
                            </Button>
                            <input
                              id="schedule-capacity-per-slot"
                              type="number"
                              min={0}
                              className="w-20 rounded-[12px] border border-input bg-background px-2 py-1.5 text-center text-sm"
                              value={field.value ?? ""}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === ""
                                    ? null
                                    : Number.parseInt(e.target.value, 10),
                                )
                              }
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() =>
                                field.onChange((field.value ?? 10) + 1)
                              }
                            >
                              +
                            </Button>
                          </div>
                        </>
                      )}
                    />
                  </div>
                  <div>
                    <RHF.Controller
                      name="cutoffMinutes"
                      control={methods.control}
                      render={({ field }) => (
                        <>
                          <label
                            htmlFor="schedule-cutoff-minutes"
                            className="mb-1 block font-medium text-muted-foreground text-sm"
                          >
                            Cutoff (ນາທີ)
                          </label>
                          <input
                            id="schedule-cutoff-minutes"
                            type="number"
                            min={0}
                            className="w-full rounded-[12px] border border-input bg-background px-3 py-2 text-sm"
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? null
                                  : Number.parseInt(e.target.value, 10),
                              )
                            }
                          />
                        </>
                      )}
                    />
                  </div>
                  <div>
                    <RHF.Controller
                      name="prepBufferMinutes"
                      control={methods.control}
                      render={({ field }) => (
                        <>
                          <label
                            htmlFor="schedule-prep-buffer-minutes"
                            className="mb-1 block font-medium text-muted-foreground text-sm"
                          >
                            Prep Buffer (ນາທີ)
                          </label>
                          <input
                            id="schedule-prep-buffer-minutes"
                            type="number"
                            min={0}
                            className="w-full rounded-[12px] border border-input bg-background px-3 py-2 text-sm"
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? null
                                  : Number.parseInt(e.target.value, 10),
                              )
                            }
                          />
                        </>
                      )}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <BranchBlackoutEditorCard control={methods.control} />
        </div>

        {/* Right column - Customer Preview */}
        <aside className="w-full shrink-0 lg:w-80">
          <Card className="rounded-xl border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <EyeIcon className="h-5 w-5 text-muted-foreground" />
                ສະແດງຕົວຢ່າງລູກຄ້າ
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                Slot ຈາກ API (ຕາມການຕັ້ງຄ່າທີ່ບັນທຶກແລ້ວ)
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label
                  htmlFor="schedule-preview-date"
                  className="mb-2 block font-medium text-muted-foreground text-sm"
                >
                  ວັນທີ preview
                </label>
                <input
                  id="schedule-preview-date"
                  type="date"
                  className="h-9 w-full rounded-[12px] border border-input bg-background px-2 text-sm"
                  value={previewDate}
                  onChange={(e) => setPreviewDate(e.target.value)}
                />
              </div>

              {branchId && pickupSlotsQuery.data ? (
                <div className="space-y-3">
                  <div className="rounded-[12px] border bg-muted/30 px-3 py-2 text-sm">
                    ASAP:{" "}
                    {pickupSlotsQuery.data.asap.available ? (
                      <span className="text-green-600">ພ້ອມ</span>
                    ) : (
                      <span className="text-muted-foreground">
                        ບໍ່ພ້ອມ
                        {pickupSlotsQuery.data.asap.reason
                          ? ` (${pickupSlotsQuery.data.asap.reason})`
                          : ""}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pickupSlotsQuery.data.slots.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        ບໍ່ມີ slot ວັນນີ້
                      </p>
                    ) : (
                      pickupSlotsQuery.data.slots.map((slot) => (
                        <span
                          key={slot.startAt}
                          className={`inline-flex rounded-[12px] border px-3 py-1.5 text-sm ${
                            slot.available
                              ? "bg-muted/50"
                              : "bg-muted/20 text-muted-foreground line-through"
                          }`}
                          title={
                            slot.reason ??
                            `${slot.bookedCount}/${slot.capacity}`
                          }
                        >
                          {slot.label}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ) : branchId && pickupSlotsQuery.isLoading ? (
                <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
              ) : null}

              <div className="border-t pt-4">
                <p className="mb-2 font-medium text-muted-foreground text-sm">
                  ຄຳນວນແບບດ່ວນ (ຕາມຟອມ)
                </p>
                <div className="flex flex-wrap gap-1">
                  {DAY_KEYS.map((key, i) => (
                    <Button
                      key={key}
                      type="button"
                      variant={previewDayIndex === i ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPreviewDayIndex(i)}
                    >
                      {DAY_LABELS[key]}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 font-medium text-muted-foreground text-sm">
                  Slot ວັນ{DAY_LABELS[previewDayKey]}
                </p>
                {previewSlots.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    ວັນນີ້ປິດບໍລິການ ຫຼື ບໍ່ມີຊ່ວງເວລາ.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <p className="mb-1 font-medium text-muted-foreground text-xs">
                        ຊ່ວງເຊົ້າ
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {morningSlots.map((slot) => (
                          <span
                            key={slot}
                            className="inline-flex rounded-[12px] border bg-muted/50 px-3 py-1.5 text-sm"
                          >
                            {formatSlotLabel(slot)}
                          </span>
                        ))}
                        {morningSlots.length === 0 && (
                          <span className="text-muted-foreground text-sm">
                            —
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 font-medium text-muted-foreground text-xs">
                        ຊ່ວງບ່າຍ
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {afternoonSlots.map((slot) => (
                          <span
                            key={slot}
                            className="inline-flex rounded-[12px] border bg-muted/50 px-3 py-1.5 text-sm"
                          >
                            {formatSlotLabel(slot)}
                          </span>
                        ))}
                        {afternoonSlots.length === 0 && (
                          <span className="text-muted-foreground text-sm">
                            —
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <p className="border-t pt-2 text-muted-foreground text-xs">
                Preview API ອັບເດດຫຼັງບັນທຶກ · ຄຳນວນແບບດ່ວນສະທ້ອນຟອມປັດຈຸບັນ
              </p>
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={onDiscard}>
          ຍົກເລີກ
        </Button>
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກທຸກການຕັ້ງຄ່າ
        </Button>
      </div>
    </FormRoot>
  );
}
